import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { RotateCcw, AlertTriangle } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Modal } from '../components/ui/Modal'
import { useActiveTenant } from '../contexts/tenant'
import { usePermissions } from '../contexts/permissions'

type ResetScope = 'all' | 'pallets' | 'fracionamento'

type StockBalance = {
  tenantId: string; productId: string; locationId: string
  quantity: number; updatedAt: string
  sku: string | null; productName: string | null; locationCode: string | null
}

type ConsRow = {
  productId: string; sku: string | null; name: string | null; unitsPerBox: number | null
  fracionamento: number; reservaCaixas: number; reservaUnidades: number
  total: number; nearestExpiry: string | null
}

type Sala = 'total' | 'frac' | 'armazem'
const SALA_LABEL: Record<Sala, string> = {
  total: 'Total',
  frac: 'Fracionamento',
  armazem: 'Armazém',
}

export function StockPage() {
  const tenantId = useActiveTenant()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const qc = useQueryClient()
  const canEdit = can('stock', 'edit')
  const verHistorico = (productId: string, sku: string | null) =>
    navigate(`/movimentacoes?productId=${productId}${sku ? `&sku=${encodeURIComponent(sku)}` : ''}`)
  const [activeTab, setActiveTab] = useState<'sala' | 'balance'>('sala')
  const [sala, setSala] = useState<Sala>('total')
  const [resetOpen, setResetOpen] = useState(false)
  const [resetScope, setResetScope] = useState<ResetScope>('all')
  const [resetConfirm, setResetConfirm] = useState('')
  const [resetting, setResetting] = useState(false)
  const [resetMsg, setResetMsg] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const term = search.trim().toLowerCase()
  const match = (sku: string | null, name: string | null) =>
    !term || (sku ?? '').toLowerCase().includes(term) || (name ?? '').toLowerCase().includes(term)

  async function runReset() {
    if (!tenantId || resetConfirm.trim().toUpperCase() !== 'ZERAR') return
    setResetting(true)
    setResetMsg(null)
    try {
      const { data } = await api.post('/stock/reset-count', { tenantId, scope: resetScope })
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['stock', 'balance', tenantId] }),
        qc.invalidateQueries({ queryKey: ['stock', 'consolidated', tenantId] }),
        qc.invalidateQueries({ queryKey: ['pallets', tenantId] }),
      ])
      setResetMsg(`Zerado: ${data.palletsDeleted} palete(s) apagado(s), ${data.fracCleared} SKU(s) do fracionamento zerado(s). Pode recontar na Contagem (celular).`)
      setResetConfirm('')
    } catch (e: any) {
      setResetMsg(e?.response?.data?.title ?? 'Erro ao zerar.')
    } finally {
      setResetting(false)
    }
  }

  const { data: balances = [], isLoading } = useQuery<StockBalance[]>({
    queryKey: ['stock', 'balance', tenantId],
    queryFn: async () => {
      if (!tenantId) return []
      return (await api.get('/stock/balance', { params: { tenantId } })).data
    },
    enabled: !!tenantId,
  })

  const { data: consolidated = [], isLoading: consLoading } = useQuery<ConsRow[]>({
    queryKey: ['stock', 'consolidated', tenantId],
    queryFn: async () => (await api.get('/replenishment/consolidated', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  // Totais por sala. Fracionamento = GERAL solto (vendável agora); armazém = caixas
  // fechadas nos paletes (pulmão); total = tudo que existe (vendável + pulmão).
  const salaTotals = consolidated.reduce(
    (acc, r) => {
      acc.fracUnits += r.fracionamento
      acc.armUnits += r.reservaUnidades
      acc.armBoxes += r.reservaCaixas
      acc.totalUnits += r.total
      return acc
    },
    { fracUnits: 0, armUnits: 0, armBoxes: 0, totalUnits: 0 },
  )
  const qtyFor = (r: ConsRow) => sala === 'frac' ? r.fracionamento : sala === 'armazem' ? r.reservaUnidades : r.total
  const salaRows = [...consolidated]
    .filter(r => match(r.sku, r.name))
    .sort((a, b) => qtyFor(b) - qtyFor(a) || (a.sku ?? '').localeCompare(b.sku ?? ''))

  const balancesFiltered = balances.filter(b => match(b.sku, b.productName))

  if (!tenantId) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-500">
        Selecione um cliente no menu lateral para continuar.
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Estoque</h2>
          <p className="text-sm text-gray-500">Saldo atual por produto e localização</p>
        </div>
        {canEdit && (
          <Button variant="secondary" size="sm" onClick={() => { setResetMsg(null); setResetConfirm(''); setResetOpen(true) }}>
            <RotateCcw className="h-4 w-4" /> Recontagem do zero
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:gap-4">
        <Card>
          <CardContent>
            <p className="text-2xl font-bold text-emerald-700">{salaTotals.fracUnits} <span className="text-base font-semibold text-gray-400">un</span></p>
            <p className="text-sm text-gray-500">Fracionamento <span className="block text-xs text-gray-400">vendável agora</span></p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">{salaTotals.armUnits} <span className="text-base font-semibold text-gray-400">un</span></p>
            <p className="text-sm text-gray-500">Armazém / pulmão <span className="block text-xs text-gray-400">{salaTotals.armBoxes} cx fechadas · abastecer p/ vender</span></p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/40">
          <CardContent>
            <p className="text-2xl font-bold text-blue-700">{salaTotals.totalUnits} <span className="text-base font-semibold text-blue-300">un</span></p>
            <p className="text-sm text-gray-500">Total em estoque <span className="block text-xs text-gray-400">vendável + pulmão</span></p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg bg-gray-100 p-1 w-fit">
        <button
          onClick={() => setActiveTab('sala')}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            activeTab === 'sala' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Por produto
        </button>
        <button
          onClick={() => setActiveTab('balance')}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            activeTab === 'balance' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Por localização
        </button>
      </div>

      {/* Busca por SKU/produto — vale para as duas abas */}
      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Buscar por SKU ou produto…"
        className="w-full max-w-sm rounded-lg border border-gray-200 px-3 py-2 text-sm"
      />

      {activeTab === 'sala' && (
        <div className="space-y-3">
          {/* Ordenação — destaca e ordena por Total, Fracionamento ou Armazém */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500">Ordenar por:</span>
            <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
              {(['total', 'frac', 'armazem'] as Sala[]).map(s => (
                <button key={s} onClick={() => setSala(s)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    sala === s ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}>
                  {SALA_LABEL[s]}
                </button>
              ))}
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              {consLoading ? (
                <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
              ) : salaRows.length === 0 ? (
                <p className="px-5 py-10 text-center text-sm text-gray-400">Nenhum produto cadastrado para este cliente.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-gray-100 bg-gray-50">
                      <tr>
                        <th className="px-5 py-3 text-left font-medium text-gray-600">SKU</th>
                        <th className="px-5 py-3 text-left font-medium text-gray-600">Produto</th>
                        <th className={`px-5 py-3 text-right font-medium ${sala === 'frac' ? 'text-blue-700' : 'text-gray-600'}`}>Fracionamento</th>
                        <th className={`px-5 py-3 text-right font-medium ${sala === 'armazem' ? 'text-blue-700' : 'text-gray-600'}`}>Armazém (cx · un)</th>
                        <th className={`px-5 py-3 text-right font-medium ${sala === 'total' ? 'text-blue-700' : 'text-gray-600'}`}>Total em estoque</th>
                        <th className="px-5 py-3 text-left font-medium text-gray-600">Validade + próx.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {salaRows.map(r => (
                        <tr key={r.productId} className="hover:bg-gray-50">
                          <td className="px-5 py-3">
                            <button onClick={() => verHistorico(r.productId, r.sku)} title="Ver histórico de movimentações"
                              className="font-mono font-medium text-blue-700 hover:underline">{r.sku ?? '—'}</button>
                          </td>
                          <td className="px-5 py-3 text-gray-700">{r.name ?? '—'}</td>
                          <td className={`px-5 py-3 text-right ${sala === 'frac' ? 'font-bold text-blue-700' : 'font-semibold text-gray-900'}`}>{r.fracionamento}</td>
                          <td className={`px-5 py-3 text-right ${sala === 'armazem' ? 'font-bold text-blue-700' : 'text-gray-600'}`}>
                            {r.reservaCaixas > 0 || r.reservaUnidades > 0
                              ? <>{r.reservaCaixas} cx · {r.reservaUnidades} un</>
                              : <span className="text-gray-300">—</span>}
                          </td>
                          <td className={`px-5 py-3 text-right ${sala === 'total' ? 'font-bold text-blue-700' : 'font-semibold text-gray-900'}`}>{r.total}</td>
                          <td className="px-5 py-3 text-gray-500">
                            {r.nearestExpiry ? new Date(r.nearestExpiry).toLocaleDateString('pt-BR') : <span className="text-gray-300">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
          <p className="px-1 text-xs text-gray-400">
            <b>Fracionamento</b> = unidades soltas no GERAL (vendável agora). <b>Armazém</b> = caixas fechadas nos paletes (pulmão),
            convertidas em unidades. <b>Total em estoque</b> = tudo que existe (vendável + pulmão) — só o fracionamento vende agora.
            A reposição do fracionamento é feita nas ondas de separação.
          </p>
        </div>
      )}

      {activeTab === 'balance' && (
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
            ) : balancesFiltered.length === 0 ? (
              <p className="px-5 py-10 text-center text-sm text-gray-400">
                {term ? 'Nenhum SKU encontrado para a busca.' : 'Nenhum saldo registrado — inicie um recebimento para movimentar estoque'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50">
                    <tr>
                      <th className="px-5 py-3 text-left font-medium text-gray-600">SKU</th>
                      <th className="px-5 py-3 text-left font-medium text-gray-600">Produto</th>
                      <th className="px-5 py-3 text-left font-medium text-gray-600">Localização</th>
                      <th className="px-5 py-3 text-right font-medium text-gray-600">Quantidade</th>
                      <th className="px-5 py-3 text-left font-medium text-gray-600">Atualizado em</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {balancesFiltered.map((b, i) => (
                      <tr key={i} className={`hover:bg-gray-50 ${b.quantity <= 10 ? 'bg-orange-50' : ''}`}>
                        <td className="px-5 py-3">
                          <button onClick={() => verHistorico(b.productId, b.sku)} title="Ver histórico de movimentações"
                            className="font-mono font-medium text-blue-700 hover:underline">{b.sku ?? '—'}</button>
                        </td>
                        <td className="px-5 py-3 text-gray-700">{b.productName ?? '—'}</td>
                        <td className="px-5 py-3 font-mono text-gray-700">{b.locationCode ?? '—'}</td>
                        <td className="px-5 py-3 text-right font-semibold text-gray-900">{b.quantity}</td>
                        <td className="px-5 py-3 text-gray-500">{new Date(b.updatedAt).toLocaleString('pt-BR')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Modal title="Recontagem do zero" open={resetOpen} onClose={() => !resetting && setResetOpen(false)}>
        <div className="space-y-4">
          <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <p>Isto <b>apaga os dados de contagem</b> deste cliente para você recomeçar do zero. <b>Não dá para desfazer.</b> Depois é só recontar na <b>Contagem (celular)</b> — os paletes novos saem com etiquetas novas.</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">O que zerar?</label>
            <div className="space-y-1.5">
              {([
                ['all', 'Tudo (paletes + fracionamento)'],
                ['pallets', 'Só os paletes (armazém)'],
                ['fracionamento', 'Só o fracionamento (soltos)'],
              ] as [ResetScope, string][]).map(([val, label]) => (
                <label key={val} className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="radio" name="resetScope" checked={resetScope === val} onChange={() => setResetScope(val)} />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Para confirmar, digite <b>ZERAR</b>
            </label>
            <input value={resetConfirm} onChange={e => setResetConfirm(e.target.value)}
              placeholder="ZERAR"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none" />
          </div>

          {resetMsg && <p className="text-sm font-medium text-gray-700">{resetMsg}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={() => setResetOpen(false)} disabled={resetting}>Cancelar</Button>
            <Button variant="danger" loading={resetting}
              disabled={resetConfirm.trim().toUpperCase() !== 'ZERAR'}
              onClick={runReset}>
              Zerar contagem
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
